import React, { useRef } from "react";
import { MapPin } from "lucide-react";
import { gsap, useGSAP, reduced } from "../../lib/motion";
import { EDUCATION, period } from "../../data/cv";

/**
 * Where it started, as a rail rather than a table.
 *
 * A list of rows treats four years of school as four database records. The
 * rail says the one thing the rows could not: that it is continuous, and that
 * it runs in a direction. The line draws itself as you scroll — `scaleY` on a
 * top-origin element, so the browser animates a transform rather than a height
 * and never reflows the column beside it.
 *
 * Newest first, because that is the order anyone reading a CV wants it in.
 */
export default function Timeline() {
  const root = useRef(null);

  useGSAP(
    () => {
      if (reduced()) return;

      gsap.fromTo(
        ".rail-line",
        { scaleY: 0 },
        {
          scaleY: 1,
          ease: "none",
          scrollTrigger: {
            trigger: root.current,
            start: "top 72%",
            end: "bottom 70%",
            scrub: 0.2,
          },
        },
      );

      gsap.utils.toArray(".stop", root.current).forEach((stop) => {
        gsap.fromTo(
          stop,
          { opacity: 0, x: -14 },
          {
            opacity: 1,
            x: 0,
            duration: 0.4,
            ease: "expo.out",
            scrollTrigger: { trigger: stop, start: "top 84%" },
          },
        );

        gsap.fromTo(
          stop.querySelector(".dot"),
          { scale: 0 },
          {
            scale: 1,
            duration: 0.32,
            ease: "back.out(2.4)",
            scrollTrigger: { trigger: stop, start: "top 84%" },
          },
        );
      });
    },
    { scope: root },
  );

  return (
    <div ref={root} className="relative pl-11 desk:pl-16">
      {/* The rail. The dim track is always there so the column never looks
          broken before the drawn line reaches it. */}
      <span
        aria-hidden
        className="absolute top-2 bottom-2 left-[15px] w-px bg-hair desk:left-[23px]"
      />
      <span
        aria-hidden
        className="rail-line absolute top-2 bottom-2 left-[15px] w-px origin-top bg-accent desk:left-[23px]"
      />

      <ol className="flex flex-col gap-10 desk:gap-14">
        {EDUCATION.map((item) => (
          <li key={`${item.degree}-${item.from}`} className="stop relative">
            <span
              aria-hidden
              className="dot absolute top-[7px] -left-11 grid size-[31px] place-items-center rounded-full border border-edge bg-bg desk:-left-16 desk:size-[47px]"
            >
              <span className="size-2 rounded-full bg-accent" />
            </span>

            <div className="min-w-0">
              <span className="kicker">{period(item)}</span>
              <h3 className="mt-2 font-display text-[19px] leading-tight font-medium tracking-[-0.045em] desk:text-[23px]">
                {item.degree}
              </h3>

              <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13.5px] font-bold text-fg">
                {item.school}
                <span className="inline-flex items-center gap-1 font-normal text-muted">
                  <MapPin aria-hidden className="size-3.5" strokeWidth={1.5} />
                  {item.place}
                </span>
              </p>

              <p className="mt-2.5 max-w-[46ch] text-[13.5px]">{item.line}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
